import { Box, Button, Card, Dialog, DialogActions, DialogContent, DialogTitle, Theme, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

export const StyledDialog = styled(Dialog)(({ theme }: { theme: Theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  },
}));

export const StyledDialogTitle = styled(DialogTitle)(({ theme }: { theme: Theme }) => ({
  paddingBottom: theme.spacing(3),
  fontSize: '1.75rem',
  fontWeight: 700,
  color: '#1976D2',
  paddingLeft: theme.spacing(4),
  paddingTop: theme.spacing(4),
}));

export const StyledDialogContent = styled(DialogContent)(({ theme }: { theme: Theme }) => ({
  paddingLeft: theme.spacing(4),
  paddingTop: 0,
}));

export const StyledDialogActions = styled(DialogActions)(({ theme }: { theme: Theme }) => ({
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  justifyContent: 'flex-end',
}));

export const ConfigContainer = styled(Box)(({ theme }: { theme: Theme }) => ({
  border: '1px solid #E0E0E0',
  borderRadius: theme.spacing(1),
  backgroundColor: 'white',
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

export const OrientationSection = styled(Box)(() => ({}));

export const OrientationContainer = styled(Box)(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  justifyContent: 'center',
  alignItems: 'center',
}));

export const OrientationCard = styled(Card)<{ 
  isSelected: boolean; 
  isDisabled: boolean; 
}>(({ theme, isSelected, isDisabled }: { theme: Theme; isSelected: boolean; isDisabled: boolean }) => ({
  cursor: isDisabled ? 'not-allowed' : 'pointer',
  border: '2px solid',
  borderColor: isSelected ? theme.palette.primary.main : theme.palette.divider,
  backgroundColor: isSelected ? `${theme.palette.primary.light}20` : theme.palette.background.paper,
  opacity: isDisabled ? 0.5 : 1,
  transition: 'all 0.2s ease-in-out',
  '&:hover': isDisabled ? {} : {    
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15), 0 4px 10px rgba(0, 0, 0, 0.1)',
    transform: 'translateY(-2px)',
  },
}));

export const PortraitCard = styled(OrientationCard)({
  width: 120,
  height: 160,
});

export const LandscapeCard = styled(OrientationCard)({
  width: 160,
  height: 120,
});

export const CardContent = styled(Box)(({ theme }: { theme: Theme }) => ({
  padding: theme.spacing(2),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
}));

export const StyledButton = styled(Button)(() => ({
  minWidth: 100,
}));

export const ExportButton = styled(Button)(() => ({
  minWidth: 120,
}));

export const MultiplePagesExportButton = styled(Button)<{ isExporting?: boolean }>(({ theme, isExporting }: { theme: Theme; isExporting?: boolean }) => ({
  minWidth: 180,
  backgroundColor: isExporting ? theme.palette.grey[400] : theme.palette.secondary.main,
  color: theme.palette.secondary.contrastText,
  cursor: isExporting ? 'not-allowed' : 'pointer',
  '&:hover': {
    backgroundColor: isExporting ? theme.palette.grey[400] : theme.palette.secondary.dark,
  },
}));

export const ExportProgressContainer = styled(Box)(({ theme }: { theme: Theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  borderRadius: theme.spacing(2),
  animation: 'fadeIn 0.3s ease-out',
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
    },
    '100%': {
      opacity: 1,
    },
  },
}));

export const ProgressText = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontSize: '1.1rem',
  fontWeight: 600,
  color: theme.palette.primary.main,
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  transition: 'all 0.3s ease-in-out',
  animation: 'fadeInUp 0.5s ease-out',
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(10px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

export const ProgressSubtext = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontSize: '0.9rem',
  color: theme.palette.text.secondary,
  textAlign: 'center',
  transition: 'all 0.3s ease-in-out',
  animation: 'fadeInUp 0.6s ease-out',
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(10px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

// Radio button styles
export const StyledRadioText = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontSize: theme.typography.h3.fontSize,
  fontWeight: theme.typography.h3.fontWeight,
  color: theme.palette.primary.dark,
}));

export const StyledRadioContainer = styled(Box)(({ theme }: { theme: Theme }) => ({
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  backgroundColor: theme.palette.common.white,
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

export const StyledRadioRow = styled(Box)(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

export const StyledSubOptionsContainer = styled(Box)(({ theme }: { theme: Theme }) => ({
  marginLeft: theme.spacing(4),
  marginBottom: theme.spacing(2),
}));

export const StyledContentContainer = styled(Box)(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
}));

export const StyledInfoIcon = styled('div')(({ theme }: { theme: Theme }) => ({
  marginTop: theme.spacing(0.5),
  flexShrink: 0,
}));
